import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Crown,
  Check,
  Users,
  SlidersHorizontal,
  PieChart,
  Download,
  Globe,
  Palette,
  Lock,
  Sparkles,
  BadgeCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme, THEMES } from "@/context/ThemeContext";

const AVANTAJE = [
  { icon: Users, title: "Grupuri mari", desc: "Peste 5 membri într-un grup — invită toată gașca." },
  { icon: SlidersHorizontal, title: "Împărțire avansată", desc: "Pe sume exacte, pe procente sau doar între anumiți membri." },
  { icon: PieChart, title: "Statistici și rapoarte", desc: "Grafice pe categorii, total pe grup și pe persoană." },
  { icon: Download, title: "Export", desc: "Descarcă cheltuielile unui grup ca CSV sau PDF." },
  { icon: Globe, title: "Conversie valutară", desc: "Cheltuieli în altă monedă, convertite automat în RON." },
  { icon: Palette, title: "Teme vizuale", desc: "Personalizează aplicația cu teme de culoare." },
];

const PLANURI = [
  { lookupKey: "chit_premium_monthly", label: "Lunar", pret: "15 RON", nota: "/lună" },
  { lookupKey: "chit_premium_yearly", label: "Anual", pret: "100 RON", nota: "/an", economie: "Economisești ~44%" },
];

export default function Premium() {
  const navigate = useNavigate();
  const {
    isPremium, activatePremium, deactivatePremium, user,
    startCheckout, cancelSubscription,
  } = useAuth();
  const { theme, setTheme } = useTheme();
  const [plataInCurs, setPlataInCurs] = useState(null);
  const [anulareInCurs, setAnulareInCurs] = useState(false);

  const isFounder = !!user?.isFounder;
  const hasSubscription = !!user?.hasSubscription;

  const handleActivate = async () => {
    try {
      await activatePremium();
      toast.success("Chit Premium activat (test, 1 zi)!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la activare");
    }
  };
  const handleDeactivate = async () => {
    try {
      await deactivatePremium();
      toast.success("Ai revenit la varianta gratuită");
    } catch {
      toast.error("Eroare");
    }
  };

  const handleCheckout = async (lookupKey) => {
    setPlataInCurs(lookupKey);
    try {
      const { checkout_url } = await startCheckout(lookupKey);
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut porni plata. Încearcă din nou.");
      setPlataInCurs(null);
    }
  };

  const handleCancelSubscription = async () => {
    setAnulareInCurs(true);
    try {
      await cancelSubscription();
      toast.success("Abonamentul se va anula la finalul perioadei plătite.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la anulare");
    } finally {
      setAnulareInCurs(false);
    }
  };

  const dataFormatata = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen pb-10">
      {/* Header cu accent auriu */}
      <header className="bg-gradient-to-b from-amber-400 to-amber-500 text-white px-5 pt-5 pb-10">
        <button
          data-testid="premium-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Înapoi"
          className="h-10 w-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
        </button>
        <div className="mt-4 flex items-center gap-2">
          <Crown className="h-8 w-8" strokeWidth={2.5} />
          <h1 className="font-heading text-3xl font-black">Chit Premium</h1>
        </div>
        <p className="text-amber-50 mt-1 text-sm">
          Deblochează tot ce ai nevoie pentru gașca ta.
        </p>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="font-heading text-4xl font-black">15 RON</span>
          <span className="text-amber-100">/lună</span>
        </div>
      </header>

      <div className="px-5 -mt-5">
        {/* Statut curent */}
        <div
          data-testid="premium-status"
          className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 flex items-center justify-between"
        >
          <div>
            <p className="text-xs text-slate-500">Contul tău</p>
            <p className="font-heading font-black text-lg text-slate-900">
              {isFounder ? "Fondator" : isPremium ? "Premium activ" : "Gratuit"}
            </p>
            {isPremium && hasSubscription && user?.premiumUntil && (
              <p data-testid="subscription-renewal-info" className="text-xs text-slate-400 mt-0.5">
                {user?.willCancelAtPeriodEnd
                  ? `Activ până pe ${dataFormatata(user.premiumUntil)} (nu se reînnoiește)`
                  : `Se reînnoiește pe ${dataFormatata(user.premiumUntil)}`}
              </p>
            )}
          </div>
          {isFounder ? (
            <span
              data-testid="founder-badge"
              className="flex items-center gap-1 text-amber-600 font-semibold text-sm"
              title="Cont de fondator — Premium gratuit pe viață"
            >
              <BadgeCheck className="h-4 w-4" /> Fondator
            </span>
          ) : isPremium ? (
            <span className="flex items-center gap-1 text-emerald-600 font-semibold text-sm">
              <Crown className="h-4 w-4" /> Premium
            </span>
          ) : (
            <span className="text-slate-400 text-sm">Free</span>
          )}
        </div>

        {/* Lista de avantaje */}
        <div className="mt-5 space-y-3">
          {AVANTAJE.map((a, i) => {
            const Icon = a.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-3 bg-white rounded-2xl border border-slate-100 p-4"
              >
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Icon className="h-5 w-5 text-amber-600" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">{a.title}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{a.desc}</p>
                </div>
                <Check className="h-5 w-5 text-emerald-500 shrink-0" strokeWidth={2.5} />
              </div>
            );
          })}
        </div>

        {/* Teme vizuale */}
        <div className="mt-5 bg-white rounded-2xl border border-slate-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="h-5 w-5 text-slate-700" strokeWidth={2.5} />
            <p className="font-semibold text-slate-900">Teme de culoare</p>
            {!isPremium && <Lock className="h-4 w-4 text-slate-400" />}
          </div>
          <div className="flex gap-3">
            {THEMES.map((t) => (
              <button
                key={t.id}
                data-testid={`theme-${t.id}`}
                disabled={!isPremium}
                onClick={() => {
                  setTheme(t.id);
                  toast.success(`Tema „${t.label}" aplicată`);
                }}
                className={`flex flex-col items-center gap-1 ${
                  !isPremium ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <span
                  className={`h-10 w-10 rounded-full border-2 ${
                    theme === t.id ? "border-slate-800" : "border-transparent"
                  }`}
                  style={{ backgroundColor: t.brand }}
                />
                <span className="text-xs text-slate-500">{t.label}</span>
              </button>
            ))}
          </div>
          {!isPremium && (
            <p className="text-xs text-slate-400 mt-2">
              Temele fac parte din Chit Premium.
            </p>
          )}
        </div>

        {/* Cont Fondator: acces Premium gratuit pe viață, fără planuri de plată */}
        {isFounder && (
          <div
            data-testid="founder-notice"
            className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center"
          >
            <p className="font-semibold text-amber-700 flex items-center justify-center gap-2">
              <BadgeCheck className="h-4 w-4" /> Cont Fondator
            </p>
            <p className="text-xs text-amber-600/80 mt-1">
              Ai acces Premium gratuit, pe viață, ca creator al aplicației.
            </p>
          </div>
        )}

        {/* Planuri de plată reale (Stripe) — ascunse pentru fondator sau abonați activi */}
        {!isFounder && !(isPremium && hasSubscription) && (
          <div
            data-testid="payment-plans-card"
            className="mt-5 bg-white rounded-2xl border border-slate-100 p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-slate-700" strokeWidth={2.5} />
              <p className="font-semibold text-slate-900">Treci pe Chit Premium</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {PLANURI.map((p) => (
                <button
                  key={p.lookupKey}
                  data-testid={`checkout-btn-${p.lookupKey}`}
                  disabled={!!plataInCurs}
                  onClick={() => handleCheckout(p.lookupKey)}
                  className="flex flex-col items-center rounded-2xl border-2 border-slate-100 hover:border-emerald-300 p-3.5 active:scale-95 transition-all disabled:opacity-50"
                >
                  <span className="text-xs font-semibold text-slate-500">{p.label}</span>
                  <span className="font-heading text-xl font-black mt-1" style={{ color: "#0f1b2d" }}>
                    {p.pret}
                  </span>
                  <span className="text-xs text-slate-400">{p.nota}</span>
                  {p.economie && (
                    <span className="text-[10px] font-semibold mt-1" style={{ color: "#12b981" }}>
                      {p.economie}
                    </span>
                  )}
                  {plataInCurs === p.lookupKey && (
                    <span className="text-[10px] text-slate-400 mt-1">Se pornește plata...</span>
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">
              Plată securizată prin Stripe. Poți anula abonamentul oricând.
            </p>
          </div>
        )}

        {/* Managementul abonamentului plătit (Stripe) */}
        {isPremium && hasSubscription && (
          <div
            data-testid="subscription-manage-card"
            className="mt-5 bg-white rounded-2xl border border-slate-100 p-4"
          >
            {user?.willCancelAtPeriodEnd ? (
              <p className="text-sm text-slate-500">
                Abonamentul tău e setat să se anuleze pe{" "}
                <span className="font-semibold text-slate-700">{dataFormatata(user.premiumUntil)}</span>.
                Rămâi Premium până atunci.
              </p>
            ) : (
              <>
                <p className="text-sm text-slate-600 mb-3">
                  Abonamentul se reînnoiește automat. Poți anula oricând, fără costuri suplimentare.
                </p>
                <button
                  data-testid="cancel-subscription-btn"
                  disabled={anulareInCurs}
                  onClick={handleCancelSubscription}
                  className="w-full py-3 rounded-2xl border border-slate-200 text-rose-600 font-semibold hover:bg-rose-50 transition-colors disabled:opacity-50"
                >
                  {anulareInCurs ? "Se anulează..." : "Anulează abonamentul"}
                </button>
              </>
            )}
          </div>
        )}

        {/* Buton activare test — doar pentru cei fără abonament plătit sau cont fondator */}
        {!isFounder && !(isPremium && hasSubscription) && (
          <div className="mt-6">
            {isPremium ? (
              <button
                data-testid="deactivate-premium-btn"
                onClick={handleDeactivate}
                className="w-full py-4 rounded-2xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
              >
                Dezactivează Premium (test)
              </button>
            ) : user?.trialUsed ? (
              <div
                data-testid="trial-used-notice"
                className="w-full py-4 rounded-2xl border border-slate-200 bg-slate-50 text-center"
              >
                <p className="font-semibold text-slate-600">
                  Ai folosit deja perioada de test
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Pentru acces permanent, treci pe Chit Premium.
                </p>
              </div>
            ) : (
              <button
                data-testid="activate-premium-btn"
                onClick={handleActivate}
                className="w-full py-4 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-base flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                <Crown className="h-5 w-5" strokeWidth={2.5} />
                Încearcă Premium gratuit 1 zi
              </button>
            )}
            {!isPremium && !user?.trialUsed && (
              <p className="text-center text-xs text-slate-400 mt-2">
                Perioada de test se poate folosi o singură dată, timp de 1 zi.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
